import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import { api } from '../auth/authStore';

export const StripeCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'cancelled' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your payment...');

  useEffect(() => {
    const handleCallback = async () => {
      const sessionId = searchParams.get('session_id');
      const cancelled = searchParams.get('cancelled');

      console.log('🔄 Stripe callback:', { sessionId, cancelled });
      console.log('📍 Current URL:', window.location.href);
      console.log('💾 Saved return URL:', sessionStorage.getItem('stripe_return_url'));

      try {
        // Build API URL
        let apiUrl = '/tenant-plans/auth-from-callback';
        if (sessionId) {
          apiUrl += `?sessionId=${sessionId}`;
        }

        console.log('🔄 Calling unified auth API:', apiUrl);

        // Get auth data from backend
        const response = await api.get(apiUrl);
        console.log('📥 API Response:', response.data);

        if (response.data.success) {
          const { user: userData, tenant: tenantData, tokens } = response.data.data;

          console.log('✅ Auth restored from callback:', { user: userData.email, tenant: tenantData.subdomain });

          // Set axios headers FIRST before updating state
          api.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
          if (tenantData?.subdomain) {
            api.defaults.headers.common['X-Tenant'] = tenantData.subdomain;
          }
          console.log('✅ Axios headers updated with new token');

          // Save to localStorage with proper structure
          const authData = {
            user: userData,
            tenant: tenantData,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
          };
          localStorage.setItem('erpca-auth', JSON.stringify(authData));
          console.log('💾 Auth data saved to localStorage:', JSON.stringify({
            userEmail: userData.email,
            tenantSubdomain: tenantData.subdomain,
            hasToken: !!tokens.accessToken
          }));

          // Update auth store with all fields
          useAuthStore.setState({
            user: userData,
            tenant: tenantData,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
          console.log('✅ Auth store updated with new state');
          
          // Verify localStorage persistence
          setTimeout(() => {
            const savedAuth = localStorage.getItem('erpca-auth');
            console.log('🔍 Verifying localStorage after 100ms:', savedAuth ? 'EXISTS' : 'MISSING');
          }, 100);

          // Set status based on whether it was cancelled
          if (cancelled === 'true') {
            setStatus('cancelled');
            setMessage('Payment was cancelled. Redirecting...');
          } else {
            setStatus('success');
            setMessage('Payment successful! Redirecting...');
          }

          // Wait a bit for auth state to settle, then redirect
          setTimeout(() => {
            console.log('🔀 Preparing to redirect after payment');
            console.log('📊 Current auth state:', useAuthStore.getState());
            
            // Determine target URL
            const isPaymentCancelled = cancelled === 'true';
            const targetPath = '/firm-admin/subscription';
            const statusParam = isPaymentCancelled ? 'cancelled' : 'success';
            
            // Check if we need to change subdomain
            const currentHostname = window.location.hostname;
            const isPlainLocalhost = currentHostname === 'localhost' || currentHostname === '127.0.0.1';
            const needsSubdomain = isPlainLocalhost && tenantData?.subdomain;
            
            if (needsSubdomain) {
              // Full page redirect to subdomain URL for development
              const port = window.location.port ? `:${window.location.port}` : '';
              const protocol = window.location.protocol;
              const targetUrl = `${protocol}//${tenantData.subdomain}.localhost${port}${targetPath}?status=${statusParam}`;
              console.log('✅ Full page redirect to subdomain URL:', targetUrl);
              window.location.href = targetUrl;
            } else {
              // We're already on the correct subdomain
              console.log('✅ On correct subdomain, using React Router to navigate to:', `${targetPath}?status=${statusParam}`);
              // Use navigate with a small delay to ensure store is ready
              navigate(`${targetPath}?status=${statusParam}`, { 
                replace: true,
                state: { 
                  paymentStatus: isPaymentCancelled ? 'cancelled' : 'success',
                  message: isPaymentCancelled
                    ? 'Payment was cancelled. You can try again.'
                    : 'Payment successful! Your subscription has been updated.'
                }
              });
            }
          }, 1000);
        } else {
          throw new Error(response.data.message || 'Failed to restore authentication');
        }
      } catch (error) {
        console.error('❌ Callback error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'An error occurred');

        // Try to redirect to saved URL anyway (user might still be authenticated)
        const returnUrl = sessionStorage.getItem('stripe_return_url');
        sessionStorage.removeItem('stripe_return_url');
        
        if (returnUrl) {
          console.log('⚠️ Auth failed but found saved URL, redirecting there:', returnUrl);
          setTimeout(() => {
            window.location.href = returnUrl;
          }, 2000);
        } else {
          // Redirect to login after 3 seconds
          setTimeout(() => {
            console.log('🔀 No saved URL, redirecting to login');
            navigate('/login', {
              replace: true,
              state: {
                message: 'Please log in to check your subscription status.',
                from: '/firm-admin/subscription'
              }
            });
          }, 3000);
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing</h2>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
            </>
          )}
          
          {status === 'cancelled' && (
            <>
              <XCircle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Cancelled</h2>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
            </>
          )}
          
          <p className="text-gray-600">{message}</p>
        </div>
      </div>
    </div>
  );
};
